import React, { useEffect, useState } from "react";
import {
  Paper, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, TablePagination, TextField, Typography, Toolbar, Button,
  CircularProgress, Box, IconButton, Chip, Tooltip
} from "@material-ui/core";
import { TableSortLabel } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import SearchIcon from "@material-ui/icons/Search";
import RefreshIcon from "@material-ui/icons/Refresh";
import AddIcon from "@material-ui/icons/Add";
import API from "../../../network/dashboardAPI";
import DeliveryCompanyDialog from "./component/DeliveryCompanyDialog";
import ActionButtons from "./component/ActionButtons";
import useAlert from "../../../services/useAlert";

const useStyles = makeStyles((theme) => ({
  root: { width: "100%", padding: theme.spacing(2) },
  toolbar: { display: "flex", justifyContent: "space-between", marginBottom: theme.spacing(2) },
  searchField: { width: 300 },
  tableContainer: { maxHeight: "calc(100vh - 300px)" },
  loadingRow: { textAlign: "center", padding: theme.spacing(4) },
  emptyRow: { textAlign: "center", padding: theme.spacing(4) },
  headerCell: { fontWeight: 600, backgroundColor: "#f5f5f5" },
  activeChip: { backgroundColor: "#e8f5e9", color: "#2e7d32" },
  inactiveChip: { backgroundColor: "#fce4ec", color: "#c62828" },
}));

export default function DeliveryCompaniesList() {
  const classes = useStyles();
  const alert = useAlert();
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [orderBy, setOrderBy] = useState("name");
  const [order, setOrder] = useState("asc");
  const [loading, setLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await API.getDeliveryCompaniesList({
        search, page: page + 1, limit: rowsPerPage,
      });
      const res = await response.json();
      if (res.success) {
        setData(res.data.items);
        setTotal(res.data.total);
      }
    } catch (error) {
      console.error("Error fetching delivery companies:", error);
    }
    setLoading(false);
  };

  useEffect(() => {
    const timer = setTimeout(() => { fetchData(); }, 500);
    return () => clearTimeout(timer);
  }, [search, page, rowsPerPage]);

  useEffect(() => { fetchData(); }, []);

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleAdd = () => { setSelectedItem(null); setDialogOpen(true); };
  const handleEdit = (item) => { setSelectedItem(item); setDialogOpen(true); };

  const handleDelete = async (row) => {
    const result = await alert.confirm(
      "Delete Delivery Company?",
      `Are you sure you want to delete "${row.name}"? This action will deactivate the company.`,
      "Yes, Delete", "Cancel"
    );
    if (!result.isConfirmed) return;
    setDeleteLoading(row.id);
    try {
      const response = await API.deleteDeliveryCompany({ id: row.id });
      const res = await response.json();
      if (res.success) {
        alert.toast("Delivery company deleted", "success");
        fetchData();
      } else {
        alert.error("Error", res.data || "Failed to delete");
      }
    } catch (error) {
      alert.error("Error", "Failed to delete delivery company");
    }
    setDeleteLoading(null);
  };

  const handleDialogSaved = () => { fetchData(); };

  const columns = [
    { id: "id", label: "ID", minWidth: 60 },
    { id: "name", label: "Name", minWidth: 150 },
    { id: "code", label: "Code", minWidth: 80 },
    { id: "contact_phone", label: "Phone", minWidth: 120 },
    { id: "contact_email", label: "Email", minWidth: 180 },
    { id: "website", label: "Website", minWidth: 180 },
    { id: "is_active", label: "Status", minWidth: 80 },
    { id: "actions", label: "Actions", minWidth: 100, sortable: false },
  ];

  return (
    <div className={classes.root}>
      <Paper elevation={1}>
        <Toolbar className={classes.toolbar}>
          <Typography variant="h6">Delivery Companies</Typography>
          <Box display="flex" alignItems="center" gap={2}>
            <TextField
              className={classes.searchField}
              size="small"
              variant="outlined"
              placeholder="Search delivery companies..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(0); }}
              InputProps={{ startAdornment: <SearchIcon style={{ marginRight: 8, color: "#999" }} /> }}
            />
            <Tooltip title="Refresh">
              <IconButton onClick={fetchData}><RefreshIcon /></IconButton>
            </Tooltip>
            <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={handleAdd}>
              Add Company
            </Button>
          </Box>
        </Toolbar>

        <TableContainer className={classes.tableContainer}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {columns.map((col) => (
                  <TableCell key={col.id} className={classes.headerCell} style={{ minWidth: col.minWidth }}>
                    {col.sortable !== false ? (
                      <TableSortLabel
                        active={orderBy === col.id}
                        direction={orderBy === col.id ? order : "asc"}
                        onClick={() => handleRequestSort(col.id)}
                      >
                        {col.label}
                      </TableSortLabel>
                    ) : col.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={columns.length} className={classes.loadingRow}>
                    <CircularProgress size={30} />
                  </TableCell>
                </TableRow>
              ) : data.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns.length} className={classes.emptyRow}>
                    <Typography color="textSecondary">No delivery companies found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                data.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>{row.id}</TableCell>
                    <TableCell><strong>{row.name}</strong></TableCell>
                    <TableCell>{row.code || "-"}</TableCell>
                    <TableCell>{row.contact_phone || "-"}</TableCell>
                    <TableCell>{row.contact_email || "-"}</TableCell>
                    <TableCell>{row.website || "-"}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={row.is_active ? "Active" : "Inactive"}
                        className={row.is_active ? classes.activeChip : classes.inactiveChip}
                      />
                    </TableCell>
                    <TableCell>
                      <ActionButtons
                        row={row}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                        deleteLoading={deleteLoading}
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value)); setPage(0); }}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </Paper>

      <DeliveryCompanyDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSaved={handleDialogSaved}
        initialData={selectedItem}
      />
    </div>
  );
}
